import React from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DatePickerWithYearMonth } from '@/components/DatePickerWithYearMonth';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Plus, Edit, Trash2, Download, Send, Search, 
  CalendarIcon, FileText, Upload, X, Save, Newspaper
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import api, { getAuthUser, getApiBaseUrl } from '@/lib/api';
import RichTextEditor from '@/components/RichTextEditor';

const CATEGORIAS = ["Executivo", "Legislativo", "Terceiro Setor"];
const SUBCATEGORIAS = {
  "Executivo": ["Leis", "Decretos", "Portarias", "Resoluções", "Extratos", "Editais", "Avisos de Licitação", "Designações", "Concursos Públicos", "Prestação de Contas"],
  "Legislativo": ["Leis", "Decretos", "Resoluções", "Portarias", "Editais", "Atas", "Pareceres"],
  "Terceiro Setor": ["Editais", "Extratos", "Prestação de Contas", "Convênios", "Termos de Parceria"]
};

export default function AdminEdicoes() {
  const [edicoes, setEdicoes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [anos, setAnos] = React.useState([]);
  const [anoSelecionado, setAnoSelecionado] = React.useState(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const user = getAuthUser();
  
  // Modal state
  const [showModal, setShowModal] = React.useState(false);
  const [editingEdicao, setEditingEdicao] = React.useState(null);
  const [dataPub, setDataPub] = React.useState(null);
  const [publicacoes, setPublicacoes] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  
  // Modal de publicação
  const [showPublishModal, setShowPublishModal] = React.useState(false);
  const [publishingEdicao, setPublishingEdicao] = React.useState(null);
  const [publishDate, setPublishDate] = React.useState(null);
  const [selectedAssinante, setSelectedAssinante] = React.useState('');
  const [assinantes, setAssinantes] = React.useState([]);
  
  // New publication form
  const [newPub, setNewPub] = React.useState({
    titulo: '',
    texto: '',
    categoria: 'Executivo',
    subcategoria: 'Decretos',
    numero: '',
    orgao: 'Prefeitura Municipal de Acaiaca'
  });

  React.useEffect(() => {
    fetchAnos();
    fetchAssinantes();
  }, []);

  React.useEffect(() => {
    if (anoSelecionado !== null) {
      fetchEdicoes();
    }
  }, [anoSelecionado]);

  const fetchAnos = async () => {
    try {
      const response = await api.get('/edicoes/anos');
      setAnos(response.data.anos);
      setAnoSelecionado(response.data.ano_atual);
    } catch (error) {
      console.error('Error fetching anos:', error);
      setAnoSelecionado(new Date().getFullYear());
    }
  };

  const fetchAssinantes = async () => {
    try {
      const response = await api.get('/config/certificados');
      if (response.data?.assinantes) {
        setAssinantes(response.data.assinantes.filter(a => a.ativo));
      }
      // Buscar assinante padrão
      const assResponse = await api.get('/config/assinatura');
      if (assResponse.data?.assinante_padrao && assResponse.data.assinante_padrao !== 'none') {
        setSelectedAssinante(assResponse.data.assinante_padrao);
      }
    } catch (error) {
      console.error('Error fetching assinantes:', error);
    }
  };

  const fetchEdicoes = async () => {
    setLoading(true);
    try {
      const params = anoSelecionado ? { ano: anoSelecionado } : {};
      const response = await api.get('/edicoes', { params });
      setEdicoes(response.data);
    } catch (error) {
      toast.error('Erro ao carregar edições');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (edicao = null) => {
    if (edicao) {
      setEditingEdicao(edicao);
      setDataPub(edicao.data_publicacao ? new Date(edicao.data_publicacao) : null);
      setPublicacoes(edicao.publicacoes || []);
    } else {
      setEditingEdicao(null);
      setDataPub(new Date());
      setPublicacoes([]);
    }
    setNewPub({
      titulo: '',
      texto: '',
      categoria: 'Executivo',
      subcategoria: 'Decretos',
      numero: '',
      orgao: 'Prefeitura Municipal de Acaiaca'
    });
    setShowModal(true);
  };

  const handleAddPub = () => {
    if (!newPub.titulo || !newPub.texto) {
      toast.error('Preencha título e texto da publicação');
      return;
    }
    
    setPublicacoes([...publicacoes, { ...newPub, ordem: publicacoes.length + 1 }]);
    setNewPub({
      titulo: '',
      texto: '',
      categoria: 'Executivo',
      subcategoria: 'Decretos',
      numero: '',
      orgao: 'Prefeitura Municipal de Acaiaca'
    });
    toast.success('Publicação adicionada');
  };

  const handleRemovePub = (index) => {
    const newPubs = [...publicacoes];
    newPubs.splice(index, 1);
    setPublicacoes(newPubs);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/upload/extract-text', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setNewPub({
        ...newPub,
        titulo: file.name.replace(/\.[^/.]+$/, ''),
        texto: response.data.texto
      });
      toast.success('Texto extraído com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao extrair texto do arquivo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleAttachPDF = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Apenas arquivos PDF são aceitos para anexo');
      e.target.value = '';
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/upload/attach-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Criar texto com link para o PDF anexado
      const pdfLink = `<p><strong>📎 PDF Anexado:</strong> <a href="${response.data.file_url}" target="_blank">${response.data.filename}</a></p>`;
      
      setNewPub({
        ...newPub,
        titulo: file.name.replace(/\.[^/.]+$/, ''),
        texto: pdfLink,
        arquivo_anexo: response.data.file_url
      });
      toast.success('PDF anexado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao anexar PDF');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async () => {
    if (publicacoes.length === 0) {
      toast.error('Adicione pelo menos uma publicação');
      return;
    }

    try {
      const submitData = {
        data_publicacao: dataPub ? dataPub.toISOString() : null,
        publicacoes: publicacoes.map((p, i) => ({
          titulo: p.titulo,
          texto: p.texto,
          categoria: p.categoria,
          subcategoria: p.subcategoria,
          numero: p.numero || null,
          orgao: p.orgao
        }))
      };

      if (editingEdicao) {
        await api.put(`/edicoes/${editingEdicao.edicao_id}`, submitData);
        toast.success('Edição atualizada com sucesso!');
      } else {
        await api.post('/edicoes', submitData);
        toast.success('Edição criada com sucesso!');
      }
      
      setShowModal(false);
      fetchEdicoes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar edição');
    }
  };

  const handleDelete = async (edicao) => {
    // Confirmação especial para edições publicadas
    if (edicao.status === 'publicado') {
      if (!user?.is_master) {
        toast.error('Apenas o administrador master pode excluir edições publicadas');
        return;
      }
      if (!window.confirm(`⚠️ ATENÇÃO: Esta edição já está PUBLICADA!\n\nExcluir a Edição nº ${edicao.numero_edicao}/${edicao.ano}?\n\nEsta ação é IRREVERSÍVEL e removerá a edição do portal público.`)) {
        return;
      }
    } else {
      if (!window.confirm(`Excluir a Edição nº ${edicao.numero_edicao}/${edicao.ano}?`)) {
        return;
      }
    }

    try {
      await api.delete(`/edicoes/${edicao.edicao_id}`);
      toast.success('Edição excluída!');
      fetchEdicoes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir edição');
    }
  };

  const handlePublish = async (edicao) => {
    // Abrir modal de publicação
    setPublishingEdicao(edicao);
    setPublishDate(edicao.data_publicacao ? new Date(edicao.data_publicacao) : new Date());
    setShowPublishModal(true);
  };

  const confirmPublish = async () => {
    if (!publishingEdicao) return;
    
    if (!selectedAssinante) {
      toast.error('Selecione um assinante para o documento');
      return;
    }

    try {
      await api.post(`/edicoes/${publishingEdicao.edicao_id}/publicar`, {
        data_publicacao: publishDate?.toISOString(),
        assinante_id: selectedAssinante
      });
      toast.success('Edição publicada com sucesso!');
      setShowPublishModal(false);
      setPublishingEdicao(null);
      fetchEdicoes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao publicar edição');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const filteredEdicoes = edicoes.filter(e => 
    String(e.numero_edicao).includes(searchTerm) ||
    e.publicacoes?.some(p => p.titulo?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const availableSubcategorias = SUBCATEGORIAS[newPub.categoria] || [];

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-edicoes">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-gray-900">Edições do DOEM</h1>
            <p className="text-gray-600">{edicoes.length} edição(ões) encontrada(s)</p>
          </div>
          
          <div className="flex gap-3 items-center">
            {/* Year Filter */}
            <Select value={String(anoSelecionado || '')} onValueChange={(v) => setAnoSelecionado(parseInt(v))}>
              <SelectTrigger className="w-32" data-testid="year-filter">
                <CalendarIcon size={16} className="mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {anos.map((ano) => (
                  <SelectItem key={ano} value={String(ano)}>{ano}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button 
              onClick={() => openModal()} 
              className="bg-[#B91C1C] hover:bg-[#991B1B]"
              data-testid="new-edition-btn"
            >
              <Plus size={18} className="mr-2" />
              Nova Edição
            </Button>
          </div>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Buscar por número ou título..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Editions List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : filteredEdicoes.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <Newspaper className="mx-auto mb-4 text-gray-300" size={48} />
              <p>Nenhuma edição encontrada</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEdicoes.map((edicao) => (
              <Card key={edicao.edicao_id} className="hover:shadow-md transition-shadow" data-testid={`edition-card-${edicao.edicao_id}`}>
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-heading font-bold text-lg">
                        Edição nº {edicao.numero_edicao}/{edicao.ano}
                      </h3>
                      <p className="text-sm text-gray-500">{formatDate(edicao.data_publicacao)}</p>
                    </div>
                    <Badge className={edicao.status === 'publicado' ? 'bg-green-600' : 'bg-yellow-600'}>
                      {edicao.status}
                    </Badge>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-4">
                    {edicao.publicacoes?.length || 0} publicação(ões)
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        // Forçar download do PDF
                        const link = document.createElement('a');
                        link.href = `${getApiBaseUrl()}/api/edicoes/${edicao.edicao_id}/pdf`;
                        link.download = `DEMOAC_Edicao_${edicao.numero_edicao}_${edicao.ano}.pdf`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                    >
                      <Download size={14} className="mr-1" />
                      PDF
                    </Button>
                    
                    {edicao.status !== 'publicado' && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => openModal(edicao)}>
                          <Edit size={14} className="mr-1" />
                          Editar
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handlePublish(edicao)}
                        >
                          <Send size={14} className="mr-1" />
                          Publicar
                        </Button>
                      </>
                    )}
                    
                    {/* Botão de exclusão - master pode excluir qualquer edição */}
                    {(edicao.status !== 'publicado' || user?.is_master) && (
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => handleDelete(edicao)}
                        title={edicao.status === 'publicado' ? 'Excluir edição publicada (admin)' : 'Excluir edição'}
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingEdicao ? `Editar Edição nº ${editingEdicao.numero_edicao}` : 'Nova Edição do DOEM'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6">
              {/* Date Picker */}
              <div>
                <Label>Data de Publicação</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full mt-1 justify-start">
                      <CalendarIcon size={16} className="mr-2" />
                      {dataPub ? format(dataPub, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar data"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <DatePickerWithYearMonth
                      selected={dataPub}
                      onSelect={setDataPub}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Publications List */}
              <div>
                <Label>Publicações ({publicacoes.length})</Label>
                <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                  {publicacoes.map((pub, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                      <div className="flex-1">
                        <div className="flex gap-2 mb-1">
                          <Badge variant="outline">{pub.categoria}</Badge>
                          <Badge variant="secondary">{pub.subcategoria}</Badge>
                        </div>
                        <p className="font-medium truncate">{pub.titulo}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleRemovePub(index)}>
                        <Trash2 size={16} className="text-red-500" />
                      </Button>
                    </div>
                  ))}
                  {publicacoes.length === 0 && (
                    <p className="text-gray-500 text-sm text-center py-4">Nenhuma publicação adicionada</p>
                  )}
                </div>
              </div>

              {/* Add Publication Form */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Adicionar Publicação</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* File Upload Options */}
                  <div>
                    <Label>Importar de arquivo</Label>
                    <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Extrair Texto */}
                      <label className="cursor-pointer">
                        <div className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg hover:border-[#B91C1C] transition-colors bg-gray-50">
                          <FileText size={20} className="text-blue-500" />
                          <div className="text-left">
                            <span className="text-sm font-medium text-gray-700 block">
                              {uploading ? 'Extraindo...' : 'Extrair Texto'}
                            </span>
                            <span className="text-xs text-gray-500">PDF, DOCX, TXT</span>
                          </div>
                        </div>
                        <input
                          type="file"
                          accept=".pdf,.docx,.txt"
                          onChange={handleFileUpload}
                          className="hidden"
                          disabled={uploading}
                        />
                      </label>
                      
                      {/* Anexar PDF */}
                      <label className="cursor-pointer">
                        <div className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg hover:border-green-600 transition-colors bg-gray-50">
                          <Upload size={20} className="text-green-500" />
                          <div className="text-left">
                            <span className="text-sm font-medium text-gray-700 block">
                              {uploading ? 'Anexando...' : 'Anexar PDF'}
                            </span>
                            <span className="text-xs text-gray-500">Manter original</span>
                          </div>
                        </div>
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={handleAttachPDF}
                          className="hidden"
                          disabled={uploading}
                        />
                      </label>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Categoria</Label>
                      <Select value={newPub.categoria} onValueChange={(v) => setNewPub({ ...newPub, categoria: v, subcategoria: SUBCATEGORIAS[v][0] })}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIAS.map((cat) => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Tipo</Label>
                      <Select value={newPub.subcategoria} onValueChange={(v) => setNewPub({ ...newPub, subcategoria: v })}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availableSubcategorias.map((sub) => (
                            <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Título *</Label>
                      <Input
                        value={newPub.titulo}
                        onChange={(e) => setNewPub({ ...newPub, titulo: e.target.value })}
                        placeholder="Ex: DECRETO Nº 001/2026"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Número (opcional)</Label>
                      <Input
                        value={newPub.numero}
                        onChange={(e) => setNewPub({ ...newPub, numero: e.target.value })}
                        placeholder="Ex: 001/2026"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Órgão</Label>
                    <Input
                      value={newPub.orgao}
                      onChange={(e) => setNewPub({ ...newPub, orgao: e.target.value })}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Texto da Publicação *</Label>
                    <div className="mt-1">
                      <RichTextEditor
                        value={newPub.texto}
                        onChange={(html) => setNewPub({ ...newPub, texto: html })}
                        placeholder="Cole ou digite o conteúdo da publicação aqui. Suporta formatação, tabelas e imagens."
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Dica: Você pode colar textos do Word, Excel ou páginas web mantendo a formatação.
                    </p>
                  </div>

                  <Button 
                    onClick={handleAddPub}
                    variant="secondary"
                    className="w-full"
                  >
                    <Plus size={18} className="mr-2" />
                    Adicionar Publicação
                  </Button>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSubmit} className="bg-[#B91C1C] hover:bg-[#991B1B]">
                  <Save size={18} className="mr-2" />
                  {editingEdicao ? 'Salvar Alterações' : 'Criar Edição'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Publish Modal - Escolher data e assinante */}
        <Dialog open={showPublishModal} onOpenChange={setShowPublishModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Send className="w-5 h-5 text-green-600" />
                Publicar Edição
              </DialogTitle>
            </DialogHeader>

            {publishingEdicao && (
              <div className="space-y-6">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-semibold">Edição nº {publishingEdicao.numero_edicao}/{publishingEdicao.ano}</p>
                  <p className="text-sm text-gray-600">{publishingEdicao.publicacoes?.length || 0} publicação(ões)</p>
                </div>

                {/* Data de Publicação */}
                <div className="space-y-2">
                  <Label>Data de Publicação *</Label>
                  <div className="flex gap-2">
                    <Input
                      type="date"
                      value={publishDate ? format(publishDate, "yyyy-MM-dd") : ""}
                      onChange={(e) => {
                        if (e.target.value) {
                          setPublishDate(new Date(e.target.value + 'T12:00:00'));
                        }
                      }}
                      className="flex-1"
                    />
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="icon" title="Escolher no calendário">
                          <CalendarIcon size={16} />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <DatePickerWithYearMonth
                          selected={publishDate}
                          onSelect={setPublishDate}
                          locale={ptBR}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <p className="text-xs text-gray-500">
                    {publishDate ? `Selecionado: ${format(publishDate, "dd/MM/yyyy", { locale: ptBR })}` : "Digite ou selecione a data"}
                  </p>
                </div>

                {/* Assinante */}
                <div className="space-y-2">
                  <Label>Assinante do Documento *</Label>
                  <Select value={selectedAssinante} onValueChange={setSelectedAssinante}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o assinante" />
                    </SelectTrigger>
                    <SelectContent>
                      {assinantes.length === 0 ? (
                        <SelectItem value="none" disabled>Nenhum assinante cadastrado</SelectItem>
                      ) : (
                        assinantes.map(a => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.nome} - {a.cargo}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {assinantes.length === 0 && (
                    <p className="text-xs text-amber-600">
                      Cadastre assinantes em Certificados e Assinatura Digital
                    </p>
                  )}
                </div>

                {/* Aviso */}
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    ⚠️ Após a publicação, a edição ficará disponível no portal público e não poderá ser editada.
                  </p>
                </div>

                {/* Botões */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowPublishModal(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    onClick={confirmPublish} 
                    className="bg-green-600 hover:bg-green-700"
                    disabled={!selectedAssinante || !publishDate}
                  >
                    <Send size={18} className="mr-2" />
                    Confirmar Publicação
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
